import type { Knex } from 'knex'
import { ensureSequencesTable } from '../../core/utils/sequence.js'

/**
 * Ensures system infrastructure tables exist.
 * Runs BEFORE module migrations.
 */
export async function ensureSystemTables(db: Knex): Promise<void> {
  // single_records: almacena entities tipo "single" (singletons)
  if (!await db.schema.hasTable('single_records')) {
    await db.schema.createTable('single_records', (table) => {
      table.string('id').primary()
      table.string('key').notNullable().unique()
      table.text('value')
      table.timestamp('created_at').defaultTo(db.fn.now())
      table.timestamp('updated_at').defaultTo(db.fn.now())
      table.string('created_by').nullable()
      table.string('updated_by').nullable()
    })
  }

  // _nexus_sequences: almacena contadores para pattern IDs
  await ensureSequencesTable(db)

  // _nexus_migrations and _nexus_migration_lock: migration tracking
  await ensureMigrationTables(db)
}

/**
 * Ensures migration tracking tables exist.
 */
export async function ensureMigrationTables(db: Knex): Promise<void> {
  // _nexus_migrations: tracks executed migrations
  if (!(await db.schema.hasTable('_nexus_migrations'))) {
    await db.schema.createTable('_nexus_migrations', (table) => {
      table.string('id', 26).primary()
      table.string('name', 255).notNullable().unique()
      table.integer('batch').notNullable()
      table.string('status', 20).notNullable() // 'running' | 'completed' | 'failed' | 'rolled_back'
      table.string('source', 100).notNullable().defaultTo('project') // 'core' | 'plugin:{name}' | 'project'
      table.timestamp('executed_at').nullable()
      table.timestamp('rolled_back_at').nullable()
      table.integer('execution_time_ms').nullable()
      table.text('error').nullable()
      table.timestamp('created_at').defaultTo(db.fn.now())
      table.timestamp('updated_at').defaultTo(db.fn.now())

      table.index(['batch'])
      table.index(['status'])
      table.index(['source'])
      table.index(['executed_at'])
    })
  } else {
    // Backwards compat: add source column if missing (pre-multisource databases)
    const hasSource = await db.schema.hasColumn('_nexus_migrations', 'source')
    if (!hasSource) {
      await db.schema.alterTable('_nexus_migrations', (table) => {
        table.string('source', 100).notNullable().defaultTo('project')
      })
    }
  }

  // _nexus_migration_lock: prevents concurrent migration execution
  if (!(await db.schema.hasTable('_nexus_migration_lock'))) {
    await db.schema.createTable('_nexus_migration_lock', (table) => {
      table.integer('id').primary().defaultTo(1)
      table.boolean('is_locked').defaultTo(false)
      table.timestamp('locked_at').nullable()
      table.string('locked_by', 100).nullable() // PID of the process

      // Ensure only one row exists
      table.check('?? = 1', ['id'])
    })

    // Insert the single lock row
    await db('_nexus_migration_lock').insert({
      id: 1,
      is_locked: false,
    })
  }
}
